import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Video, Mail, Phone, User, MessageSquare, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ConsultancyPage = ({ onBack }) => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.preferred_date || !formData.preferred_time || !formData.subject) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API}/consultancy/request`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(true);
      toast.success('Solicitação enviada com sucesso!');
    } catch (error) {
      console.error('Error submitting consultancy request:', error);
      if (error.response?.status === 403) {
        toast.error('Consultoria disponível apenas para planos Premium e Empresarial');
      } else {
        toast.error('Erro ao enviar solicitação');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user || (user.plan !== 'premium' && user.plan !== 'empresarial')) {
    return (
      <div className="consultancy-page">
        <Card className="consultancy-locked">
          <CardHeader>
            <CardTitle>Consultoria com Especialista</CardTitle>
            <CardDescription>
              Este recurso está disponível apenas para assinantes Premium e Empresarial
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="locked-content">
              <Video className="w-16 h-16 text-muted-foreground" />
              <p>Faça upgrade do seu plano para acessar consultoria com especialistas financeiros.</p>
              <Button onClick={onBack}>Ver Planos</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="consultancy-page">
        <Card className="consultancy-success">
          <CardHeader>
            <div className="success-icon">
              <CheckCircle2 className="w-16 h-16" />
            </div>
            <CardTitle>Solicitação Enviada!</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Sua solicitação de consultoria foi enviada com sucesso.</p>
            <p>Entraremos em contato pelo email <strong>{formData.email}</strong> para confirmar o agendamento.</p>
            <div className="success-details">
              <p><Calendar className="w-4 h-4" /> Data preferida: {formData.preferred_date}</p>
              <p><Clock className="w-4 h-4" /> Horário preferido: {formData.preferred_time}</p>
              <p><MessageSquare className="w-4 h-4" /> Assunto: {formData.subject}</p>
            </div>
            <Button onClick={onBack} className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao App
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="consultancy-page">
      <Button variant="ghost" onClick={onBack} className="back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Voltar
      </Button>

      <Card className="consultancy-card">
        <CardHeader>
          <div className="consultancy-header">
            <Video className="w-10 h-10" />
            <div>
              <CardTitle>Agendar Consultoria com Especialista</CardTitle>
              <CardDescription>
                Videochamada com um consultor financeiro especializado
              </CardDescription>
            </div>
            <Badge variant="outline" className="plan-badge">
              {user.plan === 'empresarial' ? 'Empresarial' : 'Premium'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="consultancy-form">
            <div className="form-row">
              <div className="form-field">
                <label><User className="w-4 h-4" /> Nome Completo *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Seu nome"
                  required
                />
              </div>
              <div className="form-field">
                <label><Mail className="w-4 h-4" /> Email *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>

            <div className="form-field">
              <label><Phone className="w-4 h-4" /> Telefone/WhatsApp *</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="(11) 99999-9999"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-field">
                <label><Calendar className="w-4 h-4" /> Data Preferida *</label>
                <Input
                  type="date"
                  value={formData.preferred_date}
                  onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div className="form-field">
                <label><Clock className="w-4 h-4" /> Horário Preferido *</label>
                <select
                  value={formData.preferred_time}
                  onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
                  className="profile-select"
                  required
                >
                  <option value="">Selecione um horário</option>
                  <option value="09:00">09:00 - 10:00</option>
                  <option value="10:00">10:00 - 11:00</option>
                  <option value="11:00">11:00 - 12:00</option>
                  <option value="14:00">14:00 - 15:00</option>
                  <option value="15:00">15:00 - 16:00</option>
                  <option value="16:00">16:00 - 17:00</option>
                  <option value="17:00">17:00 - 18:00</option>
                </select>
              </div>
            </div>

            <div className="form-field">
              <label><MessageSquare className="w-4 h-4" /> Assunto da Consultoria *</label>
              <select
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                className="profile-select"
                required
              >
                <option value="">Selecione o assunto</option>
                <option value="planejamento_financeiro">Planejamento Financeiro</option>
                <option value="sair_dividas">Estratégias para Sair das Dívidas</option>
                <option value="separacao_pf_pj">Separação PF/PJ</option>
                <option value="expansao_negocio">Expansão do Negócio</option>
                <option value="precificacao">Precificação de Produtos/Serviços</option>
                <option value="fluxo_caixa">Controle de Fluxo de Caixa</option>
                <option value="outro">Outro</option>
              </select>
            </div>

            <div className="form-field">
              <label>Mensagem Adicional (opcional)</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                placeholder="Descreva brevemente o que você gostaria de discutir na consultoria..."
                className="description-textarea"
                rows="4"
              />
            </div>

            <div className="consultancy-info">
              <h4>ℹ️ Como funciona:</h4>
              <ul>
                <li>📧 Você receberá um email de confirmação com o link da videochamada</li>
                <li>📅 A consultoria será agendada conforme sua disponibilidade</li>
                <li>⏰ Cada sessão tem duração de aproximadamente 1 hora</li>
                <li>📝 Prepare suas dúvidas e documentos relevantes</li>
              </ul>
            </div>

            <Button type="submit" disabled={loading} className="submit-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Solicitar Agendamento
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConsultancyPage;
